import {config} from "./../../config";

const Contact = () => {
    const { appName, contactDetails } = config;
    
    return (
        <div className="min-h-[calc(100vh-200px)]  px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto  overflow-hidden">
                <div className="px-6 py-8 sm:p-10">
                    {/* Header */}
                    <div className="text-center mb-10">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">Contact Us</h1>
                        <div className="w-24 h-1 bg-blue-600 mx-auto"></div>
                    </div>
                    
                    <div className="space-y-8">
                        {/* Introduction */}
                        <div className="text-center">
                            <h2 className="text-3xl font-semibold text-blue-600 mb-6">
                                Get in Touch with {appName}
                            </h2>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
                                At {appName}, we're committed to providing you with the best bus booking experience. 
                                Whether you have questions about our services, need assistance with a booking, or want to 
                                provide feedback, our team is here to help. We value your input and strive to respond to 
                                all inquiries within 24 hours during business days.
                            </p>
                            <p className="text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto mt-4">
                                Your journey matters to us, and we're dedicated to making every trip with {appName} 
                                as smooth and enjoyable as possible. Don't hesitate to reach out - we're always happy to 
                                hear from our valued customers.
                            </p>
                        </div>
                        
                        {/* Contact Details Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
                            {/* Support Contact */}
                            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600 shadow-sm hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center mb-4">
                                    <div className="bg-blue-100 p-3 rounded-full mr-4">
                                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-800">Email Support</h3>
                                </div>
                                <p className="text-gray-600 mb-2">For general inquiries and support:</p>
                                <a href={`mailto:${contactDetails.email}`} className="text-blue-600 hover:text-blue-800 font-medium text-lg">
                                    {contactDetails.email}
                                </a>
                            </div>
                            
                            {/* Phone Contact */}
                            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-green-600 shadow-sm hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center mb-4">
                                    <div className="bg-green-100 p-3 rounded-full mr-4">
                                        <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-800">Phone Support</h3>
                                </div>
                                <p className="text-gray-600 mb-2">Call us for immediate assistance:</p>
                                <a href={`tel:${contactDetails.phone.replace('+', '')}`} className="text-green-600 hover:text-green-800 font-medium text-lg">
                                    {contactDetails.phone}
                                </a>
                            </div>
                            
                            {/* Toll Free */}
                            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-purple-600 shadow-sm hover:shadow-md transition-shadow duration-300">
                                <div className="flex items-center mb-4">
                                    <div className="bg-purple-100 p-3 rounded-full mr-4">
                                        <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"></path>
                                        </svg>
                                    </div>
                                    <h3 className="text-xl font-semibold text-gray-800">Toll Free Helpline</h3>
                                </div>
                                <p className="text-gray-600 mb-2">24/7 customer care (Toll Free):</p>
                                <a href={`tel:${contactDetails.helplineNo}`} className="text-purple-600 hover:text-purple-800 font-medium text-lg">
                                    {contactDetails.helplineNo}
                                </a>
                            </div>
                        </div>
                        
                        {/* Address and Business Hours */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
                            {/* Address */}
                            <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                                <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                                    <svg className="w-6 h-6 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                    </svg>
                                    Our Office
                                </h3>
                                <p className="text-gray-600 text-lg">{contactDetails.address}</p>
                                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                                    <p className="text-gray-700">
                                        <span className="font-semibold">Note:</span> Visit our office for any in-person assistance or document verification needs.
                                    </p>
                                </div>
                            </div>
                            
                            {/* Business Hours */}
                            <div className="bg-gray-50 p-6 rounded-xl shadow-sm">
                                <h3 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                                    <svg className="w-6 h-6 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    Business Hours
                                </h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                        <span className="text-gray-600">Monday - Friday</span>
                                        <span className="font-medium text-gray-800">8:00 AM - 8:00 PM</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                        <span className="text-gray-600">Saturday</span>
                                        <span className="font-medium text-gray-800">9:00 AM - 6:00 PM</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                        <span className="text-gray-600">Sunday</span>
                                        <span className="font-medium text-gray-800">9:00 AM - 6:00 PM</span>
                                    </div>
                                    <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                        <p className="text-gray-700 text-sm">
                                            <span className="font-semibold">Emergency Support:</span> Available 24/7 via helpline for urgent booking issues.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Quick Response Promise */}
                        <div className="mt-10 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                            <div className="flex flex-col md:flex-row items-center justify-between">
                                <div>
                                    <h4 className="text-xl font-semibold text-gray-800 mb-2">Quick Response Promise</h4>
                                    <p className="text-gray-600">
                                        We guarantee a response within 24 hours for all email inquiries and immediate assistance for phone calls during business hours.
                                    </p>
                                </div>
                                <div className="mt-4 md:mt-0">
                                    <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                                        </svg>
                                        24-Hour Response Time
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Contact;